<script lang="ts" setup>
import SkrButton from '@/components/button/SkrButton.vue'
import SimpleBaseDialog from '@/components/dialogs/SimpleBaseDialog.vue'
import PaymentInfoCard from '@/components/payment/PaymentInfoCard.vue'

const emit = defineEmits<{
  close: []
  action: [data: Dialogs['DialogPaymentMethod']['contextData']]
}>()

const { t } = useI18n()
const { getShortMonthName } = useDate()

const { getCurrentPaymentMethod } = usePaymentStore()
const { id: businessId } = storeToRefs(useBusinessStore())

const paymentMethod = ref<CardDetails | null>(null)
const isLoading = ref<boolean>(false)
const isCardAdded = ref<boolean>(false)

const monthName = computed(() => {
  if (!paymentMethod.value?.card?.expiryMonth) return ''
  return getShortMonthName(paymentMethod.value.card?.expiryMonth)
})

const bankAccountLabel = computed(() => {
  if (!paymentMethod.value?.bankAccount) return ''
  return `SEPA **** ${paymentMethod.value.bankAccount.last4}`
})

const updatePaymentInfo = async () => {
  isLoading.value = true
  paymentMethod.value = await getCurrentPaymentMethod()
  isLoading.value = false
}

const { openPortal } = useChargebee({
  onClose: () => {
    updatePaymentInfo()
  },
  onPaymentSourceAdd: () => {
    isCardAdded.value = true
  },
})

const onClose = () => {
  emit('action', { isCardAdded: isCardAdded.value })
  emit('close')
}

onMounted(() => {
  updatePaymentInfo()
})
</script>

<template>
  <simple-base-dialog
    :title="t('dialog.add_payment_details.title')"
    @action="emit('action', { isCardAdded: isCardAdded })"
    @click:outside="onClose"
    @close="onClose"
  >
    <section v-if="isLoading" class="flex-grow-1 d-flex justify-center align-center">
      <skr-spinner :size="24" />
    </section>
    <div v-else>
      <payment-info-card
        v-if="paymentMethod?.bankAccount"
        icon="custom:bank"
        :empty-text="t('my_plan.add_payment_method')"
        @click="openPortal(businessId, 'PAYMENT_SOURCES')"
      >
        <template #content>
          <p>{{ bankAccountLabel }}</p>
        </template>
      </payment-info-card>
      <payment-info-card
        v-else
        icon="custom:card"
        :empty-text="t('my_plan.add_payment_method')"
        @click="openPortal(businessId, 'PAYMENT_SOURCES')"
      >
        <template v-if="paymentMethod?.card" #content>
          <p>
            {{
              t('my_plan.card_last_digits', {
                card: paymentMethod?.card?.brand,
                last_digits: paymentMethod?.card?.last4,
              })
            }}
          </p>
          <p>
            {{
              t('my_plan.card_expires', { month: monthName, year: paymentMethod?.card?.expiryYear }).replace(/^./, c =>
                c.toUpperCase()
              )
            }}
          </p>
        </template>
      </payment-info-card>
      <payment-info-card
        icon="custom:address"
        :empty-text="t('global.address_incomplete')"
        @click="openPortal(businessId, 'ADDRESS')"
      >
        <template v-if="paymentMethod?.billing" #content>
          <p>{{ paymentMethod?.billing?.address }}</p>
          <p>{{ paymentMethod?.billing?.postalCode }} {{ paymentMethod?.billing?.city }}</p>
          <p>{{ paymentMethod?.billing?.country }}</p>
        </template>
      </payment-info-card>
    </div>
    <template #actions>
      <div class="payment-details__actions">
        <skr-button size="xl" color="info" @click="onClose">
          {{ t('global.close') }}
        </skr-button>
      </div>
    </template>
  </simple-base-dialog>
</template>

<style lang="scss" scoped>
.payment-details {
  &__actions {
    margin-top: 12 * $bw;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
